import { ActivityEntityName } from '../shared/activity.entity.name';
import { AssetTypeEntity } from '../assets/asset';
import { Attachment } from '../shared/attachment';
import { CodeCategory, CodeStatus } from '../shared/code';
import { Entity } from '../entity';
import { FormPage } from './form.page';
import { GeneralSetting } from '../settings/general.settings';
import { PriorityTiny } from '../job/priority.tiny';
import { QuoteStatus } from '../quotes/quote.status';
import { Site } from '../shared/site';
import { TaskTarget } from '../tasks/task.enum';
import { WithConnectStamp } from '../connect/connect.stamp';
import { WorkOrderStatus } from '../job/workorder.status';

export enum FormTemplateStatus {
    Draft = 'Draft',
    Public = 'Public'
}

export enum FormSource {
    Office = 'Office',
    Supervisor = 'Supervisor',
    Field = 'Field'
}

export enum FormPrintLayout {
    Standard = 'Standard',
    DocumentTheme = 'Document Theme'
}

export interface FormTemplate extends WithConnectStamp {

    _id: string;
    title: string;
    subTitle: string;
    type: string;
    pages: FormPage[];
    active?: boolean;
    status: FormTemplateStatus;
    mergeWith: ActivityEntityName;
    variables?: any;
    options?: any;
    rules?: any; // TODO: Review Mongoose - was any[] but to change to any because mongoose issue https://github.com/Automattic/mongoose/issues/10617
    thankYouPage?: any;
    readonly: boolean;
    version: number;
    categories: Entity[];
    tags?: Entity[];
    backgroundColor: string;
    valid?: boolean;
    target?: TaskTarget;

    restrictions?: Entity[]; // TODO: Extend this to allow multiple types entities

    restrictionRules?: any;

    printLayout?: FormPrintLayout
    documentTheme?: Attachment;

    // For printing PDF purpose since they need dateFormat settings
    generalSetting?: GeneralSetting;

    enableLivePreview?: boolean;

    /** Whether this form template is eligible for cross-tenant claim submission */
    claimEligible?: boolean;
}

export interface FormTemplateSummary {
    _id: string;
    title: string;
    subTitle: string;
    responseCount?: number;
    mergeWith: ActivityEntityName;
    categories: Entity[];
    tags: Entity[];
    backgroundColor: string;
    valid?: boolean;
    target?: TaskTarget;

    restrictions?: Entity[];
    restrictionRules?: any;
}

export interface FormJobEntity {
    JobDescription: string;
    JobName: string;
    JobNumber: string;
    JobDueDate: string;
    JobStartDate: string;
    JobCompletedDate: string;
    JobCustomer: string;
    JobSiteAddress: string;
    JobOwner: string;
    JobPriority: PriorityTiny;
    JobStatus: Entity;
    JobType: Entity;
    JobWorkOrders: JobWorkOrder[];
    /** Every stage's lines, each carrying its stage — what a Job Items picker offers. */
    JobCodes: FormCode[];
    JobCustomFields: any;
    JobTimezone: string;
    JobBillingCompany: Entity;
    JobBillingCompanyName: string;
    JobBillingCompanyLogo: string;
    JobCustomerEmail: string;
    JobCustomerNumber: string;
    JobSiteContact: string;
    JobSiteNumber: string;
    Appointments?: { _id: string; name: string }[];
}

export interface FormWorkOrderEntity extends FormJobEntity {
    JobId: string;
    // JobDescription: string;
    // JobNumber: string;
    // JobDueDate: string;
    // JobStartDate: string;
    // JobCompletedDate: string;
    // JobCustomer: string;
    // JobSiteAddress: string;
    // JobOwner: string;
    // JobPriority: PriorityTiny;
    // JobStatus: Entity;
    // JobType: Entity;
    // JobBillingCompany: Entity;
    // JobBillingCompanyName: string;
    // JobBillingCompanyLogo: string;
    // JobCustomerEmail: string;
    // JobCustomerNumber: string;
    // JobSiteContact: string;
    // JobSiteNumber: string;
    WorkOrderNumber: string;
    WorkOrderDueDate: string;
    WorkOrderStartDate: string;
    WorkOrderCompletedDate: string;
    WorkOrderTechnician: string;
    WorkOrderTechnicianABN: string;
    WorkOrderTechnicianPhone: string;
    WorkOrderTechnicianEmail: string;
    WorkOrderResources: string;
    WorkOrderResourcesRaw: string[];
    WorkOrderName: string;
    WorkOrderStatus: Entity;
    WorkOrderCodes: FormCode[];
    WorkOrderCustomFields: any;
    WorkOrderTimezone: string;
    WorkOrderAlternateTimezone: string;
    Appointments: { _id: string; name: string }[];
}

export interface FormQuoteEntity {
    QuoteDescription: string;
    QuoteNumber: string;
    QuoteDueDate: string;
    QuoteApprovalDate: string;
    QuoteCustomer: string;
    QuoteSiteAddress: string;
    QuoteOwner: string;
    QuoteStatus: QuoteStatus;
    QuoteType: Entity;
    QuoteCodes: FormCode[];
}

export interface FormAssetEntity {
    AssetId: string;
    AssetName: string;
    AssetType: AssetTypeEntity;
    AssetParent: Entity;
    AssetSiteAddress: string;
    AssetLinkedCompany: string;
    AssetSerialNumber: string;
    AssetModel: string;
    AssetBarcode: string;
    AssetTags: string;
    AssetLinkedCrew: string;
    AssetCustomFields: any;
}

export interface FormOpportunityEntity {
    OpportunityName: string;
    OpportunityNumber: string;
    OpportunityStage: Entity;
    OpportunityType: Entity;
    OpportunityDescription: string;
    OpportunityAmount: number;
    OpportunityProbability: number;
    OpportunitySource: Entity;
    OpportunityOwner: Entity;
    OpportunityAccount: Entity;
    OpportunitySiteAddress: string;
    OpportunityExpectedClose: string;
    OpportunityTags: Entity[];
    OpportunityTimezone: string;
    OpportunityCustomFields: any;
}

export interface JobWorkOrder {
    _id: string;
    workOrderNumber: string;
    status: WorkOrderStatus;
    codes: FormCode[];
    priority: Entity;
    site?: Site;
    name?: string;
    completedDate: string;
    startDate: string;
    dueDate: string;
}

export interface FormCode {
    /** The stage / quote line id. Lines can share a `name`, so this is what identifies a picked line. */
    _id?: string;
    /** The customer system's line number, where the line came from an integration. */
    integrationId?: string;
    /** Set on job-wide (`JobCodes`) lines only: the stage the line sits on, so two identical SORs on different trades read apart. */
    workOrderId?: string;
    workOrderNumber?: string;
    workOrderName?: string;
    name: string;
    category: CodeCategory;
    description: string;
    location?: string;
    quantity?: number;
    buyPrice?: number;
    sellPrice?: number;
    status?: CodeStatus;
}


export enum EquipmentFieldPath {
    EquipmentNumber = 'EquipmentNumber',
    EquipmentName = 'EquipmentName',
    EquipmentCompanyName = 'EquipmentCompanyName',
    EquipmentStockLocation = 'EquipmentStockLocation',
    EquipmentMake = 'EquipmentMake',
    EquipmentModel = 'EquipmentModel',
    EquipmentTags = 'EquipmentTags',
    EquipmentKind = 'EquipmentKind',
    EquipmentUsage = 'EquipmentUsage',
    EquipmentStatus = 'EquipmentStatus',
    EquipmentOwnerName = 'EquipmentOwnerName',
    EquipmentSerialNumber = 'EquipmentSerialNumber',
    EquipmentInternalNumber = 'EquipmentInternalNumber',
    EquipmentCustomFields = 'EquipmentCustomFields',
    EquipmentModelYear = 'EquipmentModelYear',
    EquipmentRegoNumber = 'EquipmentRegoNumber',
    EquipmentVinNumber = 'EquipmentVinNumber',
    EquipmentType = 'EquipmentType',
}

interface FormEquipmentEntity {
    EquipmentNumber: string;
    EquipmentName: string;
    EquipmentCompanyName: string;
    EquipmentStockLocation: string;
    EquipmentMake: string;
    EquipmentModel: string;
    EquipmentTags: string[];
    EquipmentKind: string;
    EquipmentStatus: string;
    EquipmentUsage: string;
    EquipmentOwnerName: string;
    EquipmentCustomFields: any;
    EquipmentInternalNumber: string;
}

export interface FormEquipmentSerialisedEntity extends FormEquipmentEntity {
    EquipmentSerialNumber: string;
}

export interface FormEquipmentVehicleEntity extends FormEquipmentEntity {
    EquipmentModelYear: string;
    EquipmentVinNumber: string;
    EquipmentRegoNumber: string;
    EquipmentType: string;
}